import React, { useEffect, useMemo, useState } from 'react';
import { Ban, CalendarX, Check, CheckCheck, Clock, FileText, Layers } from 'lucide-react';
import { Link } from 'react-router-dom';
import AppLayout from '../../components/AppLayout';
import PageHeader from '../../components/PageHeader';
import DataTable from '../../components/DataTable';
import StatCards from '../../components/StatCards';
import usePageTitle from '../../utils/usePageTitle';
import { salesService, clientsService, clientTypesService } from '../../services/salesService';
import { productsService } from '../../services/productionService';
import api from '../../services/api';
import useLookup from '../../utils/useLookup';
import { ORIGIN_OPTIONS } from '../../constants/listOptions';
import useTableList from '../../utils/useTableList';
import { useAuth } from '../../contexts/AuthContext';
import { formatBRL, formatDateBR } from '../../lib/utils';

const STATUS_ICONS = {
    draft: { Icon: FileText, tone: 'bg-gray-100 text-gray-600' },
    approved: { Icon: Check, tone: 'bg-blue-100 text-blue-600' },
    partial: { Icon: Clock, tone: 'bg-amber-100 text-amber-600' },
    completed: { Icon: CheckCheck, tone: 'bg-green-100 text-green-600' },
    cancelled: { Icon: Ban, tone: 'bg-rose-100 text-rose-600' },
    expired: { Icon: CalendarX, tone: 'bg-amber-100 text-amber-600' },
};

const STATUS_MAP = {
    draft: { label: 'Orçado', className: 'bg-gray-100 text-gray-800' },
    approved: { label: 'Aprovado', className: 'bg-blue-100 text-blue-800' },
    partial: { label: 'Parcial', className: 'bg-amber-100 text-amber-800' },
    completed: { label: 'Concluído', className: 'bg-green-100 text-green-800' },
    cancelled: { label: 'Cancelado', className: 'bg-rose-100 text-rose-800' },
    expired: { label: 'Expirado', className: 'bg-amber-100 text-amber-800' },
};

function SalesList() {
    usePageTitle('Pedidos');
    const { can } = useAuth();
    const canSales = can('Sales');

    const list = useTableList({
        storageKey: 'sales',
        fetcher: (params) => salesService.list(params),
        errorMessage: 'Não foi possível carregar os pedidos',
    });

    const clients = useLookup(() => clientsService.list({ per_page: 1000 }));
    const clientTypes = useLookup(() => clientTypesService.list());
    const products = useLookup(() => productsService.list({ per_page: 1000 }));
    const users = useLookup(() => api.get('/users', { params: { per_page: 1000, module: 'Sales' } }));

    const [stats, setStats] = useState({});

    useEffect(() => {
        salesService.stats()
            .then((response) => setStats(response.data || {}))
            .catch((e) => console.error('Error loading sale stats:', e));
    }, []);

    const total = Object.keys(STATUS_MAP).reduce((sum, status) => sum + (stats[status] || 0), 0);
    const statCards = [
        { key: 'total', label: 'Todos', value: total, Icon: Layers, tone: 'bg-gray-100 text-gray-600' },
        ...Object.entries(STATUS_MAP).map(([status, cfg]) => ({
            key: status,
            label: cfg.label,
            value: stats[status] || 0,
            ...STATUS_ICONS[status],
        })),
    ];

    const columns = useMemo(() => ([
        { key: 'document_number', label: 'Nº', className: 'font-medium' },
        { key: 'customer_purchase_order', label: 'Ordem de compra', render: (value) => value || '-' },
        { key: 'created_at', label: 'Emissão', render: (value) => formatDateBR(value) },
        { key: 'client', label: 'Cliente', render: (value, row) => row.client?.name || '-' },
        { key: 'client_type', label: 'Tipo de cliente', render: (value, row) => row.client?.client_type?.name || '-' },
        { key: 'user', label: 'Vendedor', render: (value, row) => row.user?.name || '-' },
        { key: 'total', label: 'Valor total', className: 'tabular-nums', render: (value) => formatBRL(value) },
        { key: 'valid_until', label: 'Validade', render: (value) => (value ? formatDateBR(value) : '-') },
        {
            key: 'status',
            label: 'Status',
            render: (value) => {
                const cfg = STATUS_MAP[value] || { label: value || '-', className: 'bg-gray-100 text-gray-800' };
                return (
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${cfg.className}`}>
                        {cfg.label}
                    </span>
                );
            },
        },
    ]), []);

    const filters = [
        { key: 'document_number', label: 'Nº do pedido', type: 'text' },
        { key: 'customer_purchase_order', label: 'Ordem de compra do cliente', type: 'text' },
        { key: 'client_id', label: 'Cliente', type: 'autocomplete', options: clients.map((c) => ({ value: Number(c.id), label: c.name })) },
        { key: 'client_type_id', label: 'Tipo de cliente', type: 'select', options: clientTypes.map((t) => ({ value: String(t.id), label: t.name })) },
        { key: 'user_id', label: 'Vendedor', type: 'select', options: users.map((u) => ({ value: u.id, label: u.name })) },
        { key: 'status', label: 'Status', type: 'select', options: Object.entries(STATUS_MAP).map(([value, cfg]) => ({ value, label: cfg.label })) },
        { key: 'origin', label: 'Origem', type: 'select', options: ORIGIN_OPTIONS },
        { key: 'payment_method', label: 'Forma de pagamento', type: 'text' },
        { key: 'product_id', label: 'Item', type: 'autocomplete', options: products.map((p) => ({ value: Number(p.id), label: `${p.code} - ${p.description || ''}` })) },
        { key: 'created_from', label: 'Emissão de', type: 'date' },
        { key: 'created_to', label: 'Emissão até', type: 'date' },
        { key: 'valid_from', label: 'Validade de', type: 'date' },
        { key: 'valid_to', label: 'Validade até', type: 'date' },
    ];


    return (
        <AppLayout>
            <div className="space-y-6">
                <PageHeader
                    title="Pedidos"
                    breadcrumbs={[
                        { label: 'Dashboard', path: '/dashboard' },
                        { label: 'Vendas' },
                    ]}
                    actions={[
                        ...(canSales ? [{
                            label: 'Novo pedido',
                            as: Link,
                            to: '/sales/new',
                            icon: 'new',
                        }] : []),
                    ]}
                />

                <StatCards
                    cards={statCards}
                    className="md:grid-cols-4 xl:grid-cols-7"
                    activeKey={list.filterValues.status || 'total'}
                    onSelect={(key) => list.load(1, list.searchValue, { ...list.filterValues, status: key === 'total' ? '' : key })}
                />

                <DataTable
                    list={list}
                    columns={columns}
                    filters={filters}
                    searchPlaceholder="Buscar por número, ordem de compra ou cliente..."
                    editTo={canSales ? (row) => `/sales/${row.id}` : undefined}
                    emptyMessage="Nenhum pedido cadastrado"
                />
            </div>
        </AppLayout>
    );
}

export default SalesList;
